import React from 'react';
import type { ComponentProps } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Alert, Box, Chip, colors } from '@itandi/itandi-bb-ui';
import { DefaultV2 } from '~/components/common/layouts/DefaultV2';
import { DealLayout } from '~/components/common/layouts/deals/DealLayout';
import { useDeals } from '~/hooks/useDeals';
import { PATH as DEALS_PATH } from '~/pages/deals';
import Loading from '~/components/common/parts/loading/Loading';
import { PATH } from '~/pages/deals/[id]';
import { PATH as ESTIMATIONS_EDIT_PATH } from '~/pages/deals/[id]/estimations/edit';
import Styles from './Page.module.scss';
import { useDealEstimations } from '~/hooks/deals/useDealEstimations';
import { getDownloadPath } from '~/utils/api/deals/DealEstimationFile';
import { getDownloadPath as getHistoryDownloadPath } from '~/utils/api/deals/DealEstimationHistoryFile';
import { ConstructionTop } from '~/components/common/constructions/top/ConstructionTop';
import { useDealConstructionItems } from '~/hooks/useDealConstructionItems';
import { ConstructionList } from '~/components/common/constructions/list/ConstructionList';
import { update as updateEnabled } from '~/utils/api/deals/DealEstimation';
import type {
  DealConstruction,
  DealConstructionContext
} from '~/models/DealConstruction';
import {
  IN_CONSTRUCTION,
  IN_ESTIMATION,
  isOwnStatus,
  OWN_SEND_ESTIMATE,
  SEND_ESTIMATE
} from '~/models/Status';
import { getOriginVendor } from '~/models/DealVendorCompany';
import { useIdQueryHandler } from '~/hooks/useIdQueryHandler';
import { ConstructionConfirmedFooter } from '~/components/common/constructions/ConstructionConfirmedFooter';
import useItandiAccount from '~/hooks/useItandiAccount';
import {
  updateAccept,
  updateReject
} from '~/utils/api/deals/DealEstimationHeader';
import type { NskResponse } from '~/models/Api';
import { update as updateStatus } from '~/utils/api/DealStatus';
import { useDealEstimationHeaders } from '~/hooks/deals/useDealEstimationHeaders';
import { useDealEstimationHistories } from '~/hooks/deals/useDealEstimationHistories';
import { estimationRequestSide } from '~/models/DealEstimationHeader';
import { estimationHeaderStatus } from '~/models/Deal';
import { VENDOR_COMPANY_ITANDI_ID } from '~/models/VendorCompany';

// 下記ステータスの場合のみ施工対象を変更できる
const allowedStatuses: number[] = [SEND_ESTIMATE, OWN_SEND_ESTIMATE];

export const Page: React.FC = () => {
  // TODO: 環境変数かDBで管理する
  // https://itandi-dev.atlassian.net/browse/NSKDEV-1960
  const getEditableConstructionTargetVendorIds = (): Array<number> => {
    if (process.env.NEXT_PUBLIC_NSK_ENV !== 'production') {
      return [
        VENDOR_COMPANY_ITANDI_ID, // イタンジ株式会社
        9 // 検証用
      ];
    }
    return [
      VENDOR_COMPANY_ITANDI_ID, // イタンジ株式会社
      127, // 株式会社セイコー
      128, // 株式会社三和テレム
      159, // イケダ商事合同会社
      161, // 株式会社ハウスクリニック
      195, // 株式会社関東リノベーション
      196, // 株式会社トミーズマート
      197 // 株式会社ステップスリー
    ];
  };
  const id = useIdQueryHandler();
  const router = useRouter();
  const { data: currentAccount } = useItandiAccount().currentAccount();
  const { data: estimationContext, mutate: mutateConstructions } =
    useDealEstimations().index(id);
  const { data: deal, mutate: mutateDeal } = useDeals().show(id);
  const { data: dealConstructionItems } = useDealConstructionItems();
  const { data: dealEstimationHistoryContext } =
    useDealEstimationHistories().index(id);
  const { data: dealEstimationHeaders, mutate: mutateHeaders } =
    useDealEstimationHeaders().index(id);
  const requestHeader =
    dealEstimationHeaders?.find((header) => header.status === 'request') ??
    null;
  const { data: dealEstimationRequestHistoryContext, isLoading } =
    useDealEstimationHeaders().show(id, requestHeader && requestHeader.id);
  const isReadonly = React.useMemo(() => {
    if (deal === null) {
      return true;
    }
    if (requestHeader !== null) return true;
    return !allowedStatuses.some((s) => s === deal.status);
  }, [deal, requestHeader]);
  React.useEffect(() => {
    if (deal === null) return;
    if (!isOwnStatus(deal.status) && estimationRequestSide(deal, 'client')) {
      router.replace(ESTIMATIONS_EDIT_PATH(deal.id)).then();
    }
  }, [deal, router]);
  if (
    id === null ||
    deal === null ||
    isLoading ||
    estimationContext === null ||
    dealEstimationHistoryContext === null ||
    dealConstructionItems === null ||
    dealEstimationHeaders === null ||
    currentAccount === null
  ) {
    return <Loading isLoading />;
  }
  const getConstructionContextList = (): DealConstructionContext => {
    if (isOwnStatus(deal.status)) {
      if (
        dealEstimationHistoryContext &&
        dealEstimationHistoryContext?.dealConstructions.length !== 0
      ) {
        return dealEstimationHistoryContext;
      }
      return estimationContext;
    }
    return dealEstimationRequestHistoryContext ?? estimationContext;
  };
  const handleMutateConstructions = async (): Promise<void> => {
    await mutateConstructions?.();
    await mutateHeaders?.();
    await mutateDeal?.();
  };
  const handleClickPreview = (): void => {
    const path = getDownloadPath(id);
    window.open(path);
  };
  const handleClickHistoryPreview = (): void => {
    if (requestHeader === null) {
      throw new Error('requestHeader cannot be null');
    }
    const path = getHistoryDownloadPath(deal.id, requestHeader.id);
    window.open(path);
  };
  const items = [
    { label: '案件一覧', href: DEALS_PATH, as: Link },
    { label: '案件詳細', href: PATH(deal.id), as: Link },
    { label: '見積り確認' }
  ] as const satisfies ComponentProps<typeof DealLayout>['items'];
  const handleUpdateEnabled = async (
    newConstruction: DealConstruction
  ): Promise<void> => {
    if (newConstruction.type !== 'DealEstimation') {
      return;
    }
    await updateEnabled(newConstruction, newConstruction.id, deal.id);
  };
  const handleAcceptUpdateStatus = async (): Promise<NskResponse<void>> => {
    if (requestHeader === null) {
      throw new Error('requestHeader cannot be null');
    }
    return updateAccept(deal.id, requestHeader.id);
  };
  const handleUpdateRejectStatus = async (): Promise<NskResponse<void>> => {
    if (requestHeader === null) {
      throw new Error('requestHeader cannot be null');
    }
    return updateReject(deal.id, requestHeader.id);
  };
  const handleAcceptForward = async (): Promise<NskResponse<void>> =>
    updateStatus(deal.id, IN_CONSTRUCTION, null, null);
  const canEditConstructionEnabled = (): boolean => {
    const vendorCompany = getOriginVendor(deal);
    // 非共有案件は施工対象を変更できる
    if (vendorCompany === null) return true;

    return getEditableConstructionTargetVendorIds().includes(vendorCompany.id);
  };
  return (
    <DefaultV2>
      <DealLayout deal={deal} isVendor={false} items={items}>
        <div className={Styles.EstimateHeader}>
          <h1 className={Styles.EstimateTitle}>見積り確認</h1>
          {estimationHeaderStatus(deal).label !== '' && (
            <Chip
              background={estimationHeaderStatus(deal).color}
              borderColor={null}
              label={estimationHeaderStatus(deal).label}
              maxWidth="100%"
              textColor={colors['itandi-white']}
            />
          )}
        </div>
        {estimationHeaderStatus(deal).description !== '' &&
          deal.status !== IN_ESTIMATION && (
            <Box width="50%">
              <Alert>{estimationHeaderStatus(deal).description}</Alert>
            </Box>
          )}
        <ConstructionTop
          constructionContext={estimationContext}
          dealEstimationHistoryContext={dealEstimationRequestHistoryContext}
          handleClickEstimationHistoryPreview={
            requestHeader ? handleClickHistoryPreview : null
          }
          handleClickEstimationPreview={handleClickPreview}
          ownManagement={isOwnStatus(deal.status)}
          readonly={isReadonly}
        />
        <ConstructionList
          companyType="client"
          constructionContext={getConstructionContextList()}
          deal={deal}
          dealConstructionItems={dealConstructionItems}
          docType="estimation"
          handleMutateConstructions={handleMutateConstructions}
          handleUpdateEnabled={
            canEditConstructionEnabled() ? handleUpdateEnabled : null
          }
          readonly={isReadonly}
          showEstimationConfirmedButton={
            (requestHeader && !isOwnStatus(deal.status)) ?? false
          }
          showHighlight={!isOwnStatus(deal.status)}
        />
        <ConstructionConfirmedFooter
          account={currentAccount}
          companyType="client"
          deal={deal}
          dealEstimationContext={estimationContext}
          dealEstimationHeader={requestHeader}
          handleAcceptForward={handleAcceptForward}
          handleMutate={handleMutateConstructions}
          handleUpdateAcceptStatus={handleAcceptUpdateStatus}
          handleUpdateRejectStatus={handleUpdateRejectStatus}
          isAdmin={currentAccount.isAdmin}
        />
      </DealLayout>
    </DefaultV2>
  );
};
